import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, Megaphone, Layers, CheckCircle2, RefreshCw,
  Mail, MessageCircle, Smartphone, MessagesSquare,
  Globe, Search, Camera, Flame, PieChart, Activity,
  ArrowUpRight, ArrowDownRight, Sun, Moon,
  Plus, UserSquare2, BarChart3, Inbox, TrendingUp,
} from 'lucide-react';
import { useTheme } from '../components/Navbar';

const API = 'https://api.martechlabs.in';
const greet = () => { const h = new Date().getHours(); return h<12?'Good morning':h<17?'Good afternoon':'Good evening'; };
const fmtNum = (n) => n==null?'0':Number(n).toLocaleString('en-IN');
const fmtDate = (d) => { if(!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB',{timeZone:'Asia/Kolkata',day:'2-digit',month:'short',year:'numeric'}); } catch{return '—';} };
const getToken = () => localStorage.getItem('martech_token')||localStorage.getItem('token')||'';
const getHdrs  = () => ({ Authorization:`Bearer ${getToken()}`,'Content-Type':'application/json' });

const CHANNELS = [
  { key:'email',     label:'Email',         color:'#3B82F6', Icon:Mail },
  { key:'whatsapp',  label:'WhatsApp',      color:'#10B981', Icon:MessageCircle },
  { key:'sms',       label:'SMS',           color:'#F59E0B', Icon:Smartphone },
  { key:'rcs',       label:'RCS',           color:'#8B5CF6', Icon:MessagesSquare, soon:true },
  { key:'facebook',  label:'Facebook Ads',  color:'#2563EB', Icon:Globe },
  { key:'google',    label:'Google Ads',    color:'#EF4444', Icon:Search },
  { key:'instagram', label:'Instagram Ads', color:'#EC4899', Icon:Camera, soon:true },
  { key:'heatmap',   label:'Heatmap',       color:'#F97316', Icon:Flame },
];

const TK = {
  light: {
    pageBg:'#F4F5F8', card:'#FFFFFF', border:'#E9EBF0',
    text:'#0E1320', sub:'#5B6472', muted:'#9AA1AE',
    accent:'#5B4EE8', accentBg:'#EEF0FD', accentTxt:'#4338CA',
    row:'#FAFBFC', rowBdr:'#F0F2F5',
    btnPri:'#5B4EE8', btnPriH:'#4C3FD4', btnPriTxt:'#fff',
    btnSec:'#fff', btnSecTxt:'#374151', btnSecBdr:'#E9EBF0',
    up:'#059669', upBg:'#D1FAE5', dn:'#DC2626', dnBg:'#FEE2E2',
    toggleBg:'#E9EBF0', toggleActive:'#fff',
    errBg:'#FEF2F2', errBdr:'#FECACA', errTxt:'#DC2626',
    skBg:'#ECEEF3', shine:'rgba(255,255,255,0.65)',
    cardShadow:'0 10px 28px rgba(14,19,32,0.08)',
  },
  dark: {
    pageBg:'#0B0B12', card:'#14141F', border:'#212133',
    text:'#F1F2F6', sub:'#9499AE', muted:'#565E70',
    accent:'#7C72FF', accentBg:'rgba(124,114,255,0.12)', accentTxt:'#A5A0FF',
    row:'#191926', rowBdr:'#212133',
    btnPri:'#6C63FF', btnPriH:'#5B53E8', btnPriTxt:'#fff',
    btnSec:'#1A1A28', btnSecTxt:'#D1D5DB', btnSecBdr:'#212133',
    up:'#34D399', upBg:'rgba(5,150,105,0.2)', dn:'#F87171', dnBg:'rgba(220,38,38,0.2)',
    toggleBg:'#1A1A28', toggleActive:'#232336',
    errBg:'rgba(220,38,38,0.12)', errBdr:'rgba(220,38,38,0.3)', errTxt:'#F87171',
    skBg:'#1C1C2B', shine:'rgba(255,255,255,0.06)',
    cardShadow:'0 10px 28px rgba(0,0,0,0.45)',
  },
};

/* animated count-up — only re-animates when the target actually changes */
function useCountUp(target, dur=900){
  const [v,setV]=useState(0);
  const ref=useRef(0);
  useEffect(()=>{
    const from=ref.current, to=Number(target)||0;
    if(from===to){ setV(to); return; }
    let raf, start;
    const tick=(t)=>{
      if(!start) start=t;
      const p=Math.min((t-start)/dur,1);
      const e=1-Math.pow(1-p,3);
      setV(from+(to-from)*e);
      if(p<1) raf=requestAnimationFrame(tick);
      else { ref.current=to; setV(to); }
    };
    raf=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(raf);
  },[target,dur]);
  return v;
}
function AnimatedNumber({ value, decimals=0, suffix='' }){
  const v=useCountUp(value);
  const n=decimals? Number(v.toFixed(decimals)) : Math.round(v);
  return <>{n.toLocaleString('en-IN')}{suffix}</>;
}

function Skel({ w='60%', h=14, tk }){
  return <div className="mts-sk" style={{width:w,height:h,background:tk.skBg,borderRadius:6}}/>;
}

function KpiCard({ Icon, label, value, decimals, suffix, sub, accent, delta, loading, tk, i }){
  const has=!loading && delta!=null && Number(delta)!==0;
  const up=Number(delta)>=0;
  return (
    <div className="mts-card mts-enter" style={{animationDelay:`${i*60}ms`,background:tk.card,border:`1px solid ${tk.border}`,borderRadius:14,padding:'16px 18px',display:'flex',flexDirection:'column',gap:12}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div style={{width:36,height:36,borderRadius:10,background:accent+'18',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <Icon size={18} color={accent} strokeWidth={2}/>
        </div>
        {has&&<span style={{display:'inline-flex',alignItems:'center',gap:2,background:up?tk.upBg:tk.dnBg,color:up?tk.up:tk.dn,fontSize:11,fontWeight:600,padding:'3px 8px',borderRadius:20}}>
          {up?<ArrowUpRight size={11}/>:<ArrowDownRight size={11}/>}{Math.abs(Number(delta))}%
        </span>}
      </div>
      <div>
        {loading
          ? <div style={{padding:'4px 0 2px'}}><Skel w="62%" h={26} tk={tk}/></div>
          : <div style={{fontSize:30,fontWeight:700,color:tk.text,letterSpacing:'-0.6px',lineHeight:1}}>
              <AnimatedNumber value={value} decimals={decimals} suffix={suffix}/>
            </div>}
        <div style={{fontSize:10.5,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.05em',marginTop:8}}>{label}</div>
        {sub&&!loading&&<div style={{fontSize:10.5,color:tk.muted,marginTop:4}}>{sub}</div>}
      </div>
    </div>
  );
}

function ChannelCard({ Icon, label, value, accent, soon, loading, tk, i }){
  return (
    <div className="mts-card mts-enter" style={{animationDelay:`${i*45}ms`,background:tk.card,border:`1px solid ${tk.border}`,borderRadius:13,padding:'13px 15px',position:'relative'}}>
      {soon&&<span style={{position:'absolute',top:11,right:11,background:tk.accentBg,color:tk.accentTxt,fontSize:8,fontWeight:700,padding:'2px 6px',borderRadius:20,textTransform:'uppercase',letterSpacing:'0.05em'}}>Soon</span>}
      <div style={{width:30,height:30,borderRadius:8,background:accent+'18',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:11}}>
        <Icon size={15} color={accent} strokeWidth={2}/>
      </div>
      {loading
        ? <Skel w="50%" h={18} tk={tk}/>
        : <div style={{fontSize:22,fontWeight:700,color:tk.text,letterSpacing:'-0.3px',lineHeight:1}}><AnimatedNumber value={value}/></div>}
      <div style={{fontSize:11,color:tk.sub,marginTop:5,fontWeight:500}}>{label}</div>
    </div>
  );
}

/* dependency-free donut */
function Donut({ data, total, tk }){
  const size=158, stroke=22, r=(size-stroke)/2, C=2*Math.PI*r;
  let acc=0;
  return (
    <div style={{position:'relative',width:size,height:size,flexShrink:0}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size/2} ${size/2})`}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={tk.skBg} strokeWidth={stroke}/>
          {total>0 && data.map((d,i)=>{
            const len=(d.value/total)*C;
            const el=(
              <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
                stroke={d.color} strokeWidth={stroke}
                strokeDasharray={`${len} ${C-len}`} strokeDashoffset={-acc}
                style={{transition:'stroke-dasharray .7s ease, stroke-dashoffset .7s ease'}}/>
            );
            acc+=len;
            return el;
          })}
        </g>
      </svg>
      <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
        <div style={{fontSize:24,fontWeight:700,color:tk.text,letterSpacing:'-0.5px',lineHeight:1}}>
          {total>0? <AnimatedNumber value={total}/> : '0'}
        </div>
        <div style={{fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.05em',marginTop:4}}>Sends</div>
      </div>
    </div>
  );
}

function Badge({ status }){
  const m={sent:['#DBEAFE','#1D4ED8'],delivered:['#D1FAE5','#065F46'],failed:['#FEE2E2','#991B1B'],pending:['#FEF3C7','#92400E'],scheduled:['#EDE9FE','#5B21B6']};
  const [bg,color]=m[status?.toLowerCase()]||['#F3F4F6','#374151'];
  return <span style={{background:bg,color,fontSize:11,fontWeight:600,padding:'2px 9px',borderRadius:20,textTransform:'capitalize',whiteSpace:'nowrap'}}>{status||'—'}</span>;
}

function ThemeToggle({ tk }){
  const { theme, setTheme } = useTheme();
  const btn = (t,Icon,label) => (
    <button type="button" key={t} onClick={()=>setTheme(t)} style={{display:'flex',alignItems:'center',gap:5,background:theme===t?tk.toggleActive:'transparent',border:'none',borderRadius:6,padding:'5px 10px',cursor:'pointer',fontSize:12,fontWeight:500,color:theme===t?tk.text:tk.sub,boxShadow:theme===t?'0 1px 3px rgba(0,0,0,0.08)':'none',transition:'all 0.15s',whiteSpace:'nowrap'}}>
      <Icon size={13}/>{label}
    </button>
  );
  return <div style={{display:'flex',background:tk.toggleBg,borderRadius:8,padding:3}}>{btn('light',Sun,'Light')}{btn('dark',Moon,'Dark')}</div>;
}

function Empty({ Icon, title, sub, cta, onCta, tk }){
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'26px 16px',textAlign:'center'}}>
      <div style={{width:40,height:40,borderRadius:10,background:tk.accentBg,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12}}>
        <Icon size={18} color={tk.accentTxt} strokeWidth={1.8}/>
      </div>
      <div style={{fontSize:13,fontWeight:600,color:tk.text,marginBottom:4}}>{title}</div>
      <div style={{fontSize:12,color:tk.muted,marginBottom:cta?14:0,maxWidth:210}}>{sub}</div>
      {cta&&<button type="button" onClick={onCta} style={{background:tk.btnPri,color:tk.btnPriTxt,border:'none',borderRadius:7,padding:'7px 14px',fontSize:12,fontWeight:600,cursor:'pointer'}}>{cta}</button>}
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { theme } = useTheme();
  const tk = TK[theme];
  const user = (()=>{ try{return JSON.parse(localStorage.getItem('martech_user')||'{}');}catch{return{};} })();

  const [kpi,     setKpi]     = useState({profiles:0,segments:0,campaigns:0});
  const [deltas,  setDeltas]  = useState({});
  const [delivery,setDelivery]= useState({delivered:0,sent:0,failed:0,pending:0,delivered_rate:0});
  const [ch,      setCh]      = useState({});
  const [segs,    setSegs]    = useState([]);
  const [camps,   setCamps]   = useState([]);
  const [busy,    setBusy]    = useState(true);
  const [err,     setErr]     = useState('');

  const load = useCallback(async (silent=false) => {
    if (!getToken()) { nav('/login'); return; }
    if (!silent) setBusy(true);
    setErr('');
    try {
      const h = { headers: getHdrs() };
      // ONE call/5s = 12/min — leaves budget for every other page
      const d = (await axios.get(`${API}/api/v1/vendor/dashboard`, h)).data;
      setKpi({ profiles:d.total_profiles??0, segments:d.total_segments??0, campaigns:d.total_campaigns??0 });
      setDeltas(d.deltas||{});
      setDelivery(d.delivery||{delivered:0,sent:0,failed:0,pending:0,delivered_rate:0});
      setCh(d.channels||{});

      if (!silent) {
        try { setSegs(((await axios.get(`${API}/api/v1/segments`,h)).data?.segments||[]).slice(0,6)); } catch{}
        try {
          const [schedR, emailR] = await Promise.allSettled([
            axios.get(`${API}/api/v1/campaign/schedule/list`, h),
            axios.get(`${API}/api/v1/email/events`, h),
          ]);
          const scheduled = schedR.status==='fulfilled'
            ? (schedR.value.data?.scheduled||schedR.value.data?.data||schedR.value.data?.campaigns||[]) : [];
          const emailEvents = emailR.status==='fulfilled'
            ? (emailR.value.data?.events||[]) : [];
          const combined = [
            ...scheduled.map(r=>({
              key:String(r.schedule_id||r.id||`s_${r.scheduled_at}_${r.segment_name}`),
              title:r.template_name||r.segment_name||r.name||'Campaign',
              sub:r.segment_name||'all', channel:(r.channel||'whatsapp').toLowerCase(),
              status:r.status||'pending', date:r.scheduled_at,
            })),
            ...emailEvents.map(r=>({
              key:String(r.campaign_event_id||r.id||`e_${r.sent_at}_${r.campaign_name}`),
              title:r.campaign_name||'Campaign', sub:'email',
              channel:(r.channel||'email').toLowerCase(), status:r.status||'sent', date:r.sent_at,
            })),
          ];
          combined.sort((a,b)=> new Date(b.date||0)-new Date(a.date||0));
          setCamps(combined.slice(0,5));
        } catch{}
      }
    } catch(e) {
      if(e.response?.status===401){localStorage.clear();nav('/login');}
      else if(!silent) setErr('Could not reach the API. Check your connection.');
    } finally { if(!silent) setBusy(false); }
  }, [nav]);

  useEffect(()=>{
    load();
    const id=setInterval(()=>load(true),5000);
    return ()=>clearInterval(id);
  },[load]);

  const openNewCampaign = () => nav('/campaigns',{state:{openNew:true}});
  const attempted = (delivery.delivered||0)+(delivery.sent||0)+(delivery.failed||0);

  const mix = CHANNELS.filter(c=>(ch[c.key]||0)>0).map(c=>({label:c.label,value:ch[c.key],color:c.color}));
  const mixTotal = mix.reduce((s,d)=>s+d.value,0);

  const dh = [
    {label:'Delivered',value:delivery.delivered||0,color:'#10B981'},
    {label:'Sent',     value:delivery.sent||0,     color:'#3B82F6'},
    {label:'Failed',   value:delivery.failed||0,   color:'#EF4444'},
    {label:'Pending',  value:delivery.pending||0,  color:'#F59E0B'},
  ];
  const dhTotal = dh.reduce((s,d)=>s+d.value,0);

  const cardBase = {background:tk.card,border:`1px solid ${tk.border}`,borderRadius:14,padding:'18px 20px'};

  return (
    <>
      <style>{`
        .mts-dash{padding:22px 26px}
        .mts-kpi{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:22px}
        .mts-ch{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:22px}
        .mts-insights{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:22px}
        .mts-actions{display:flex;gap:8px;margin-bottom:22px;flex-wrap:wrap}
        .mts-tables{display:grid;grid-template-columns:1fr 1fr;gap:14px}
        .mts-hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:22px;gap:12px}
        .mts-hdr-right{display:flex;align-items:center;gap:8px;flex-shrink:0}
        .mts-tbl{width:100%;border-collapse:collapse;font-size:12px}
        .mts-card{transition:transform .18s ease, box-shadow .18s ease}
        .mts-card:hover{transform:translateY(-2px);box-shadow:var(--cardShadow)}
        .mts-sk{position:relative;overflow:hidden}
        .mts-sk::after{content:'';position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,var(--shine),transparent);animation:mtsSh 1.5s infinite}
        @keyframes mtsSh{100%{transform:translateX(100%)}}
        .mts-enter{animation:mtsIn .5s cubic-bezier(.22,1,.36,1) both}
        @keyframes mtsIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
        @media(max-width:1024px){
          .mts-kpi{grid-template-columns:repeat(2,1fr)}
          .mts-ch{grid-template-columns:repeat(2,1fr)}
          .mts-insights{grid-template-columns:1fr}
          .mts-tables{grid-template-columns:1fr}
        }
        @media(max-width:640px){
          .mts-dash{padding:14px 16px}
          .mts-kpi{grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:16px}
          .mts-ch{grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:16px}
          .mts-insights{gap:12px;margin-bottom:16px}
          .mts-tables{gap:12px}
          .mts-hdr{flex-direction:column;gap:10px;margin-bottom:16px}
          .mts-hdr-right{width:100%;justify-content:space-between}
          .mts-actions{gap:6px;margin-bottom:16px}
          .mts-tbl{font-size:11px}
          .mts-hide-sm{display:none!important}
        }
        @media(prefers-reduced-motion:reduce){
          .mts-enter{animation:none}
          .mts-sk::after{animation:none}
          .mts-card:hover{transform:none}
        }
      `}</style>

      <div className="mts-dash" style={{background:tk.pageBg,minHeight:'100vh',boxSizing:'border-box','--shine':tk.shine,'--cardShadow':tk.cardShadow}}>

        <div className="mts-hdr">
          <div>
            <h1 style={{margin:0,fontSize:23,fontWeight:700,color:tk.text,letterSpacing:'-0.4px'}}>
              {greet()}, {user.name||user.email?.split('@')[0]||'Admin'} 👋
            </h1>
            <p style={{margin:'4px 0 0',fontSize:13,color:tk.sub}}>
              {new Date().toLocaleDateString('en-GB',{timeZone:'Asia/Kolkata',weekday:'long',day:'2-digit',month:'long',year:'numeric'})}
            </p>
          </div>
          <div className="mts-hdr-right">
            <ThemeToggle tk={tk}/>
            <button type="button" onClick={()=>load()} title="Refresh"
              style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:8,width:34,height:34,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:tk.sub,flexShrink:0}}>
              <RefreshCw size={14}/>
            </button>
            <div style={{display:'flex',alignItems:'center',gap:6,background:tk.card,border:`1px solid ${tk.border}`,borderRadius:20,padding:'6px 12px',fontSize:12,fontWeight:600,color:tk.text,flexShrink:0}}>
              <span style={{width:7,height:7,borderRadius:'50%',background:'#10B981',display:'inline-block'}}/>
              Live
            </div>
          </div>
        </div>

        {err&&<div style={{background:tk.errBg,border:`1px solid ${tk.errBdr}`,color:tk.errTxt,padding:'10px 14px',borderRadius:8,fontSize:13,marginBottom:16}}>{err}</div>}

        {/* KPI */}
        <div className="mts-kpi">
          <KpiCard i={0} Icon={Users}        label="Total Profiles" value={kpi.profiles}  accent="#6C63FF" delta={deltas.profiles}  loading={busy} tk={tk}/>
          <KpiCard i={1} Icon={Megaphone}    label="Campaigns"      value={kpi.campaigns} accent="#3B82F6" delta={deltas.campaigns} loading={busy} tk={tk}/>
          <KpiCard i={2} Icon={Layers}       label="Segments"       value={kpi.segments}  accent="#10B981" delta={deltas.segments}  loading={busy} tk={tk}/>
          <KpiCard i={3} Icon={CheckCircle2} label="Delivery Rate"  value={delivery.delivered_rate||0} decimals={1} suffix="%"
            sub={`${fmtNum(delivery.delivered||0)} of ${fmtNum(attempted)} sent`} accent="#F59E0B" delta={deltas.delivered} loading={busy} tk={tk}/>
        </div>

        {/* Channel Activity */}
        <div style={{fontSize:10.5,fontWeight:700,color:tk.muted,letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:11}}>Channel Activity</div>
        <div className="mts-ch">
          {CHANNELS.map((c,i)=>(
            <ChannelCard key={c.key} i={i} Icon={c.Icon} label={c.label} value={ch[c.key]??0} accent={c.color} soon={c.soon} loading={busy} tk={tk}/>
          ))}
        </div>

        {/* Insights: Channel Mix donut + Delivery Health */}
        <div className="mts-insights">
          <div className="mts-card mts-enter" style={{...cardBase,animationDelay:'80ms'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
              <PieChart size={16} color={tk.accent}/>
              <span style={{fontSize:14,fontWeight:600,color:tk.text}}>Channel Mix</span>
            </div>
            {busy?(
              <div style={{display:'flex',gap:18,alignItems:'center',flexWrap:'wrap'}}>
                <div className="mts-sk" style={{width:158,height:158,borderRadius:'50%',background:tk.skBg}}/>
                <div style={{flex:1,minWidth:150,display:'flex',flexDirection:'column',gap:10}}>
                  {[0,1,2].map(k=><Skel key={k} w="80%" h={12} tk={tk}/>)}
                </div>
              </div>
            ):mix.length===0?(
              <Empty Icon={PieChart} title="No sends yet" sub="Channel distribution appears once campaigns go out." tk={tk}/>
            ):(
              <div style={{display:'flex',gap:20,alignItems:'center',flexWrap:'wrap'}}>
                <Donut data={mix} total={mixTotal} tk={tk}/>
                <div style={{flex:1,minWidth:150,display:'flex',flexDirection:'column',gap:9}}>
                  {mix.map((d,i)=>{
                    const pct=Math.round((d.value/mixTotal)*100);
                    return (
                      <div key={i} style={{display:'flex',alignItems:'center',gap:9}}>
                        <span style={{width:9,height:9,borderRadius:3,background:d.color,flexShrink:0}}/>
                        <span style={{fontSize:12.5,color:tk.text,fontWeight:500,flex:1}}>{d.label}</span>
                        <span style={{fontSize:12,color:tk.sub,fontWeight:600}}>{fmtNum(d.value)}</span>
                        <span style={{fontSize:11,color:tk.muted,width:34,textAlign:'right'}}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="mts-card mts-enter" style={{...cardBase,animationDelay:'140ms'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
              <Activity size={16} color={tk.accent}/>
              <span style={{fontSize:14,fontWeight:600,color:tk.text}}>Delivery Health</span>
            </div>
            {busy?(
              <div style={{display:'flex',flexDirection:'column',gap:14}}>
                <Skel w="100%" h={14} tk={tk}/>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  {[0,1,2,3].map(k=><Skel key={k} w="70%" h={12} tk={tk}/>)}
                </div>
              </div>
            ):(
              <>
                <div style={{display:'flex',width:'100%',height:14,borderRadius:8,overflow:'hidden',background:tk.skBg,marginBottom:18}}>
                  {dhTotal>0 ? dh.map((d,i)=>(
                    d.value>0 && <div key={i} title={`${d.label}: ${fmtNum(d.value)}`} style={{width:`${(d.value/dhTotal)*100}%`,background:d.color,transition:'width .7s ease'}}/>
                  )) : null}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 16px'}}>
                  {dh.map((d,i)=>(
                    <div key={i} style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{width:9,height:9,borderRadius:3,background:d.color,flexShrink:0}}/>
                      <span style={{fontSize:12,color:tk.sub,flex:1}}>{d.label}</span>
                      <span style={{fontSize:13,fontWeight:700,color:tk.text}}><AnimatedNumber value={d.value}/></span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mts-actions">
          <button type="button" onClick={openNewCampaign}
            onMouseEnter={e=>e.currentTarget.style.background=tk.btnPriH}
            onMouseLeave={e=>e.currentTarget.style.background=tk.btnPri}
            style={{display:'inline-flex',alignItems:'center',gap:6,background:tk.btnPri,color:tk.btnPriTxt,border:'none',borderRadius:9,padding:'10px 16px',fontSize:13,fontWeight:600,cursor:'pointer',transition:'background 0.15s'}}>
            <Plus size={14}/> New Campaign
          </button>
          <button type="button" onClick={()=>nav('/profiles')}
            style={{display:'inline-flex',alignItems:'center',gap:6,background:tk.btnSec,color:tk.btnSecTxt,border:`1px solid ${tk.btnSecBdr}`,borderRadius:9,padding:'10px 16px',fontSize:13,fontWeight:500,cursor:'pointer'}}>
            <UserSquare2 size={14}/> View Profiles
          </button>
          <button type="button" onClick={()=>nav('/analytics')}
            style={{display:'inline-flex',alignItems:'center',gap:6,background:tk.btnSec,color:tk.btnSecTxt,border:`1px solid ${tk.btnSecBdr}`,borderRadius:9,padding:'10px 16px',fontSize:13,fontWeight:500,cursor:'pointer'}}>
            <BarChart3 size={14}/> Analytics
          </button>
        </div>

        {/* Tables */}
        <div className="mts-tables">
          <div className="mts-card mts-enter" style={{...cardBase,animationDelay:'80ms',padding:'16px 18px',overflowX:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <span style={{fontSize:14,fontWeight:600,color:tk.text}}>Recent Campaigns</span>
              <button type="button" onClick={()=>nav('/campaigns')} style={{background:'none',border:'none',color:tk.accent,fontSize:12,fontWeight:600,cursor:'pointer',padding:0,whiteSpace:'nowrap'}}>View all →</button>
            </div>
            {busy?(
              <div style={{display:'flex',flexDirection:'column',gap:12,padding:'6px 0'}}>{[0,1,2,3].map(k=><Skel key={k} w="100%" h={16} tk={tk}/>)}</div>
            ):camps.length===0?(
              <Empty Icon={Inbox} title="No campaigns yet" sub="Launch your first campaign." cta="+ New Campaign" onCta={openNewCampaign} tk={tk}/>
            ):(
              <table className="mts-tbl">
                <thead>
                  <tr>
                    {['Campaign','Channel','Status'].map(hd=>(
                      <th key={hd} style={{textAlign:'left',padding:'6px 8px',fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.06em',borderBottom:`1px solid ${tk.border}`}}>{hd}</th>
                    ))}
                    <th className="mts-hide-sm" style={{textAlign:'right',padding:'6px 8px',fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.06em',borderBottom:`1px solid ${tk.border}`}}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {camps.map((c,i)=>{
                    const name=(c.title||'Campaign').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());
                    return (
                      <tr key={c.key||i} style={{background:i%2?tk.row:'transparent'}}>
                        <td style={{padding:'9px 8px',borderBottom:`1px solid ${tk.rowBdr}`}}>
                          <div style={{fontWeight:500,color:tk.text}}>{name}</div>
                          <div style={{fontSize:10,color:tk.muted,marginTop:1}}>→ {c.sub||'all'}</div>
                        </td>
                        <td style={{padding:'9px 8px',borderBottom:`1px solid ${tk.rowBdr}`}}>
                          <span style={{background:tk.accentBg,color:tk.accentTxt,fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:5,textTransform:'capitalize'}}>{c.channel||'—'}</span>
                        </td>
                        <td style={{padding:'9px 8px',borderBottom:`1px solid ${tk.rowBdr}`}}><Badge status={c.status||'pending'}/></td>
                        <td className="mts-hide-sm" style={{padding:'9px 8px',textAlign:'right',color:tk.sub,borderBottom:`1px solid ${tk.rowBdr}`}}>{fmtDate(c.date)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="mts-card mts-enter" style={{...cardBase,animationDelay:'140ms',padding:'16px 18px',overflowX:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <span style={{fontSize:14,fontWeight:600,color:tk.text}}>Top Segments</span>
              <button type="button" onClick={()=>nav('/segments')} style={{background:'none',border:'none',color:tk.accent,fontSize:12,fontWeight:600,cursor:'pointer',padding:0,whiteSpace:'nowrap'}}>View all →</button>
            </div>
            {busy?(
              <div style={{display:'flex',flexDirection:'column',gap:12,padding:'6px 0'}}>{[0,1,2,3].map(k=><Skel key={k} w="100%" h={16} tk={tk}/>)}</div>
            ):segs.length===0?(
              <Empty Icon={Layers} title="No segments yet" sub="Create segments to target your audience." cta="+ New Segment" onCta={()=>nav('/segments')} tk={tk}/>
            ):(
              <table className="mts-tbl">
                <thead>
                  <tr>
                    <th style={{textAlign:'left',padding:'6px 8px',fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.06em',borderBottom:`1px solid ${tk.border}`}}>Segment</th>
                    <th style={{textAlign:'right',padding:'6px 8px',fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.06em',borderBottom:`1px solid ${tk.border}`}}>Members</th>
                    <th className="mts-hide-sm" style={{textAlign:'right',padding:'6px 8px',fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.06em',borderBottom:`1px solid ${tk.border}`}}>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {segs.map((s,i)=>{
                    const name=(s.segment_name||'').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());
                    return (
                      <tr key={i} style={{background:i%2?tk.row:'transparent'}}>
                        <td style={{padding:'9px 8px',fontWeight:500,color:tk.text,borderBottom:`1px solid ${tk.rowBdr}`}}>{name}</td>
                        <td style={{padding:'9px 8px',textAlign:'right',borderBottom:`1px solid ${tk.rowBdr}`}}>
                          <span style={{background:'#D1FAE5',color:'#065F46',fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:5}}>{fmtNum(s.count??s.member_count??0)}</span>
                        </td>
                        <td className="mts-hide-sm" style={{padding:'9px 8px',textAlign:'right',borderBottom:`1px solid ${tk.rowBdr}`}}>
                          <TrendingUp size={13} color="#10B981" style={{display:'inline'}}/>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}